var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
import { IsArray, IsBoolean, IsDateString, IsEnum, IsNumber, IsOptional, IsString, IsUUID, ValidateNested, } from 'class-validator';
import { AppointmentStatus } from '../../appointments/enums/appointment-status.enum.js';
import { AttendanceOutcome } from '../../appointments/enums/attendance-outcome.enum.js';
import { CancellationOrigin } from '../../appointments/enums/cancellation-origin.enum.js';
import { TreatmentPlanPillDto } from '../../patient-list/dto/treatment-plan-pill.dto.js';
import { TreatmentUnitStatus } from '../../treatment-plan/enums/treatment-unit-status.enum.js';
import { PatientDocumentationItemType } from '../enums/patient-documentation-item-type.enum.js';
/** One file of a documentation entry, in the order it was attached. */
export class DocumentationAttachmentDto {
    /** The `FileUpload` it is; a replacement entry keeps it by this id (`keptFileIds`). */
    fileId;
    /** The stored name, which the download endpoint takes. */
    filename;
    /** The name the file was uploaded with, as the chip shows it. */
    originalFilename;
}
__decorate([
    IsUUID(),
    Expose(),
    __metadata("design:type", String)
], DocumentationAttachmentDto.prototype, "fileId", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationAttachmentDto.prototype, "filename", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationAttachmentDto.prototype, "originalFilename", void 0);
export class DocumentationViewItemDto {
    id;
    type;
    text;
    /** Every file of this version, in order. Versions share files: a replacement keeps what it lists. */
    attachments;
    authorName;
    createdAt;
    struckOutAt;
    replacesItemId;
    treatmentPlanId;
}
__decorate([
    IsUUID(),
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewItemDto.prototype, "id", void 0);
__decorate([
    IsEnum(PatientDocumentationItemType),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewItemDto.prototype, "type", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewItemDto.prototype, "text", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => DocumentationAttachmentDto),
    Expose(),
    __metadata("design:type", Array)
], DocumentationViewItemDto.prototype, "attachments", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewItemDto.prototype, "authorName", void 0);
__decorate([
    IsDateString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewItemDto.prototype, "createdAt", void 0);
__decorate([
    IsOptional(),
    IsDateString(),
    Expose(),
    __metadata("design:type", Object)
], DocumentationViewItemDto.prototype, "struckOutAt", void 0);
__decorate([
    IsOptional(),
    IsUUID(),
    Expose(),
    __metadata("design:type", Object)
], DocumentationViewItemDto.prototype, "replacesItemId", void 0);
__decorate([
    IsOptional(),
    IsUUID(),
    Expose(),
    __metadata("design:type", Object)
], DocumentationViewItemDto.prototype, "treatmentPlanId", void 0);
export class DocumentationProgressEntryDto {
    abbreviation;
    code;
    /** The prescribed Heilmittelkatalog option (GKV); null without one. Name it with `heilmittelLabel`. */
    optionName;
    sequencesInPlan;
    authorizedUnitCount;
    status;
    treatmentUnitIds;
}
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DocumentationProgressEntryDto.prototype, "abbreviation", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationProgressEntryDto.prototype, "code", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DocumentationProgressEntryDto.prototype, "optionName", void 0);
__decorate([
    IsArray(),
    IsNumber({}, { each: true }),
    Expose(),
    __metadata("design:type", Array)
], DocumentationProgressEntryDto.prototype, "sequencesInPlan", void 0);
__decorate([
    IsNumber(),
    Expose(),
    __metadata("design:type", Number)
], DocumentationProgressEntryDto.prototype, "authorizedUnitCount", void 0);
__decorate([
    IsEnum(TreatmentUnitStatus),
    Expose(),
    __metadata("design:type", String)
], DocumentationProgressEntryDto.prototype, "status", void 0);
__decorate([
    IsArray(),
    IsUUID(undefined, { each: true }),
    Expose(),
    __metadata("design:type", Array)
], DocumentationProgressEntryDto.prototype, "treatmentUnitIds", void 0);
export class DocumentationProgressDto {
    entries;
}
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => DocumentationProgressEntryDto),
    Expose(),
    __metadata("design:type", Array)
], DocumentationProgressDto.prototype, "entries", void 0);
export class DocumentationViewAppointmentDto {
    appointmentId;
    date;
    startTime;
    endTime;
    treatmentProgress;
    items;
    treatmentPlanIds;
    appointmentStatus;
    /** This patient's recorded Terminstatus at the appointment; null while none is. */
    attendance;
    /** Who cancelled this patient's place, when it was cancelled; null otherwise. */
    cancellationOrigin;
    /**
     * The appointment asks this patient for a documentation: the `documentation_missing`
     * gate (`isDictationAllowed` — started, not cancelled, not a no-show). Only such
     * appointments, and those with an entry, are in the view.
     */
    documentationAsked;
    /**
     * The patient's Terminstatus is still open (`attendance_unconfirmed` not done):
     * saving or dictating the documentation confirms „Erschienen".
     */
    attendanceUnconfirmed;
}
__decorate([
    IsUUID(),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewAppointmentDto.prototype, "appointmentId", void 0);
__decorate([
    IsDateString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewAppointmentDto.prototype, "date", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewAppointmentDto.prototype, "startTime", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewAppointmentDto.prototype, "endTime", void 0);
__decorate([
    IsOptional(),
    ValidateNested(),
    Type(() => DocumentationProgressDto),
    Expose(),
    __metadata("design:type", Object)
], DocumentationViewAppointmentDto.prototype, "treatmentProgress", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => DocumentationViewItemDto),
    Expose(),
    __metadata("design:type", Array)
], DocumentationViewAppointmentDto.prototype, "items", void 0);
__decorate([
    IsArray(),
    IsUUID(undefined, { each: true }),
    Expose(),
    __metadata("design:type", Array)
], DocumentationViewAppointmentDto.prototype, "treatmentPlanIds", void 0);
__decorate([
    IsEnum(AppointmentStatus),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewAppointmentDto.prototype, "appointmentStatus", void 0);
__decorate([
    IsOptional(),
    IsEnum(AttendanceOutcome),
    Expose(),
    __metadata("design:type", Object)
], DocumentationViewAppointmentDto.prototype, "attendance", void 0);
__decorate([
    IsOptional(),
    IsEnum(CancellationOrigin),
    Expose(),
    __metadata("design:type", Object)
], DocumentationViewAppointmentDto.prototype, "cancellationOrigin", void 0);
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], DocumentationViewAppointmentDto.prototype, "documentationAsked", void 0);
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], DocumentationViewAppointmentDto.prototype, "attendanceUnconfirmed", void 0);
export class DocumentationViewFreeEntryGroupDto {
    date;
    items;
}
__decorate([
    IsDateString(),
    Expose(),
    __metadata("design:type", String)
], DocumentationViewFreeEntryGroupDto.prototype, "date", void 0);
__decorate([
    IsArray(),
    Type(() => DocumentationViewItemDto),
    Expose(),
    __metadata("design:type", Array)
], DocumentationViewFreeEntryGroupDto.prototype, "items", void 0);
export class DocumentationViewResponseDto {
    appointments;
    freeEntries;
    plans;
}
__decorate([
    IsArray(),
    Type(() => DocumentationViewAppointmentDto),
    Expose(),
    __metadata("design:type", Array)
], DocumentationViewResponseDto.prototype, "appointments", void 0);
__decorate([
    IsArray(),
    Type(() => DocumentationViewFreeEntryGroupDto),
    Expose(),
    __metadata("design:type", Array)
], DocumentationViewResponseDto.prototype, "freeEntries", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => TreatmentPlanPillDto),
    Expose(),
    __metadata("design:type", Array)
], DocumentationViewResponseDto.prototype, "plans", void 0);
//# sourceMappingURL=documentation-view.dto.js.map